import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { finalize, first } from 'rxjs/operators';
import {
    IDataRequest,
    IDuaDetails,
    IDuaDocuments,
    IResearchTeam,
    IVirtualMachineAccessInvitation
} from '@vivli/features/data-requests/infrastructure/interface';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useMyDataRequestsApiContext,
    useToastApiContext,
    useUsersApiContext,
    useVirtualMachineApiContext
} from '@vivli/core/infrastructure/context';
import { useTranslation } from 'react-i18next';
import { IVirtualMachine } from '@vivli/features/virtual-machine/infrastructure/interface';
import { DataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { DataRequestStatusEnum, RequestedStudyTypeEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useFormContext, UseFormReturn, useFormState } from 'react-hook-form';
import { PermissionsEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { useNavigate, useParams } from 'react-router';
import { useInitializeResearchTeamHook } from '@vivli/features/data-requests/infrastructure/hook';
import { findDuplicates } from '@vivli/shared/infrastructure/hook';
import { IStatusUpdate, IUser } from '@vivli/shared/infrastructure/interface';
import { IUserEmailParam } from '@vivli/features/users/infrastructure/interface';

interface DataRequestContextWrapperProps {
    children: ReactNode;
    dataRequest: IDataRequest;
}

export const DataRequestContextWrapper = ({ children, dataRequest }: DataRequestContextWrapperProps) => {
    const { organizations } = useOrganizations();
    const { status } = useParams();
    const navigate = useNavigate();
    const { user, hasPermission, orgHasPermission } = useActiveUserContext();
    const { t } = useTranslation();
    const { transformDataRequest } = useInitializeResearchTeamHook();
    const { updateMyDataRequest } = useMyDataRequestsApiContext();
    const formApi = useFormContext<IDataRequest>();
    const { isDirty } = useFormState();

    const [dataRequestState, setDataRequest] = useState<IDataRequest>(dataRequest);
    const [showApprovalButtons, setShowApprovalButtons] = useState<boolean>(false);
    const [virtualMachine, setVirtualMachine] = useState<IVirtualMachine>();
    const [showResearchResults, setShowResearchResults] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isSavingDataRequest, setIsSavingDataRequest] = useState(false);
    const [isSubmittingDataRequest, setIsSubmittingDataRequest] = useState(false);
    const [statusUpdateSignal, setStatusUpdateSignal] = useState(false);
    const [isAdminEditing, setIsAdminEditing] = useState(false);
    const [planningTableData, setPlanningTableData] = useState([]);
    const [duaDetails, setDuaDetails] = useState<IDuaDetails>();
    const [duaDocuments, setDuaDocuments] = useState<IDuaDocuments[]>([]);
    const [statusUpdateState, setStatusUpdateState] = useState<IStatusUpdate>();
    const [includeRiskScore, setIncludeRiskScore] = useState<boolean>(false);
    const [surveyHasErrors, setSurveyHasErrors] = useState<boolean>(false);

    const useService = useUsersApiContext();

    const { isAmr } = useAssignedAppTypeContext();
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);

    const ownsDataRequest = () => {
        return dataRequest.userId === user.userId;
    };

    const dataRequestsService = useDataRequestsApiContext();
    const virtualMachineService = useVirtualMachineApiContext();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const isSavingRef = useRef(false);

    const updateShowResearchResults = () => {
        const showResearchResultsState =
            (status === 'InProgress' ||
                dataRequestState?.status === DataRequestStatusEnum.PartiallyFulfilled ||
                dataRequestState?.status === DataRequestStatusEnum.Fulfilled ||
                status === 'Fulfilled' ||
                status === 'Active') &&
            !!virtualMachine;

        setShowResearchResults(showResearchResultsState);
    };

    const isActiveUserOrgAdmin = (): boolean => {
        return (
            user.orgMemberships.find(
                (om) =>
                    orgHasPermission(om.orgId, PermissionsEnum.ProviderActAdv) &&
                    dataRequest.requestedStudies.some((a) => a.organizationId === om.orgId)
            ) !== undefined
        );
    };
    const updateShowApprovalButtons = () => {
        if (!dataRequestState) {
            return;
        }

        //TODO PERMFE - redo with permissions+
        const isIrpApprover =
            user.orgMemberships.find(
                (om) =>
                    orgHasPermission(om.orgId, PermissionsEnum.IRPApprove) &&
                    orgHasPermission(om.orgId, PermissionsEnum.ProviderActAdv) &&
                    dataRequest.requestedStudies.some((a) => a.approvalBodyOrgId == om.orgId)
            ) !== undefined;
        const isDataProvider =
            user.orgMemberships.find(
                (om) =>
                    orgHasPermission(om.orgId, PermissionsEnum.DataContrib) &&
                    orgHasPermission(om.orgId, PermissionsEnum.ProviderActAdv) &&
                    dataRequest.requestedStudies.some((a) => a.organizationId == om.orgId)
            ) !== undefined;

        switch (dataRequestState.status) {
            case DataRequestStatusEnum.AwaitingIRPApproval:
            case DataRequestStatusEnum.RejectedByIRP:
            case DataRequestStatusEnum.RevisionsRequestedByIRP:
                setShowApprovalButtons(isIrpApprover);
                break;
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
                setShowApprovalButtons(isDataProvider);
                break;
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.RejectedByVivli:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.DUAReturnedToRequester:
            case DataRequestStatusEnum.Approved:
                setShowApprovalButtons(isVivliAdmin);
                break;
            default:
                setShowApprovalButtons(false);
                break;
        }
    };

    //Methods for data-request form components

    const updateIsSaving = (status: boolean) => {
        isSavingRef.current = status;
        setIsSavingDataRequest(status);
    };

    const handleError = (error: string) => {
        const cleanError = error.replace(/\\r|\\n|"/g, ' ').trim();
        modalService.error(cleanError);
        updateIsSaving(false);
        setIsSubmittingDataRequest(false);
    };

    const handleDataRequestFormUpdate = (updatedDataRequest: IDataRequest) => {
        updateIsSaving(false);
        formApi.reset(updatedDataRequest, { keepErrors: true, keepIsValid: true });
        setDataRequest(updatedDataRequest);
    };

    const handleDataPackageCreation = () => {
        if (!dataRequest.metaDataDataPackageId) {
            dataRequestsService
                .createDataPackageForRequest(dataRequest.id)
                .pipe(first())
                .subscribe((updatedDataRequest) => {
                    handleDataRequestFormUpdate({
                        ...updatedDataRequest,
                        researchTeam: dataRequest.researchTeam, // data package creation nulls out research team
                        researchProposal: dataRequest.researchProposal // data package creation nulls out research team
                    });
                });
        }
    };

    const submitDataRequest = (updatedDataRequest: IDataRequest) => {
        const routeToNavigate = isAmr
            ? '/mydatarequests/active' //AMR uses My Data Requests
            : '/admin/requests/active'; //CT does not
        setIsSubmittingDataRequest(true);
        dataRequestsService
            .submitDataRequest(updatedDataRequest)
            .pipe(first())
            .subscribe((resultDataRequest) => {
                if (isAmr) {
                    updateMyDataRequest(resultDataRequest);
                }
                navigate(routeToNavigate);
            }, handleError);
    };

    const handleConfirmStep = (updatedDataRequest: IDataRequest) => {
        if (updatedDataRequest.requestedStudies.some((a) => a.requestedStudyType === RequestedStudyTypeEnum.External)) {
            modalService.confirm(t(TranslationKeyEnum.pathway2SubmitConfirmText), {
                title: 'Confirm Data Request is Ready to Submit',
                onConfirm: () => {
                    submitDataRequest(updatedDataRequest);
                },
                onCancel: () => {
                    handleDataRequestFormUpdate(updatedDataRequest);
                }
            });
        } else {
            submitDataRequest(updatedDataRequest);
        }
    };

    const handleDataRequestSaved = (updatedDataRequest: IDataRequest, submitRequest: boolean) => {
        toastService.success(`${updatedDataRequest.requestTitle} successfully saved`);

        if (submitRequest) {
            handleConfirmStep(updatedDataRequest);
        } else {
            handleDataRequestFormUpdate(updatedDataRequest);
        }
    };

    const getInvitations = (invitationsDataRequest: IDataRequest): Promise<IVirtualMachineAccessInvitation[]> => {
        // Currently, we only check for the disabled status on the front end; other validations are handled by the back end.
        // Can we move this to back-end too in the future?

        return new Promise((resolve) => {
            const teamMembers = invitationsDataRequest.researchTeam.researchTeamDetails;

            // we are excluding disabled account from valid invitation array
            const validInvitations: IVirtualMachineAccessInvitation[] = [];

            const requests = teamMembers.map((teamMember) => {
                if (
                    teamMember.inviteToVirtualMachine &&
                    !teamMember.hasBeenInvitedToVMAccess &&
                    !teamMember.hasAccessToVirtualMachineResource
                ) {
                    const emailParam: IUserEmailParam = { emailAddress: teamMember.email };

                    return useService
                        .getUserByEmail(emailParam)
                        .pipe(first())
                        .toPromise()
                        .then((user: IUser) => {
                            if (user.disabled) {
                                modalService.error(
                                    `The Vivli account for user "${teamMember.email}" is disabled.  Only users with active Vivli Accounts can be activated for access to this request.`
                                );
                                return null;
                            }

                            return {
                                dataRequestId: invitationsDataRequest.id,
                                email: teamMember.email,
                                InvitedById: user.userId,
                                teamMemberId: teamMember.teamMemberId
                            };
                        })
                        .catch((error) => {
                            modalService.error(`Error fetching user: ${teamMember.email}`);
                            return null;
                        });
                }
                return Promise.resolve(null);
            });

            Promise.all(requests).then((results) => {
                validInvitations.push(...results.filter((invitation) => invitation !== null));
                resolve(validInvitations);
            });
        });
    };

    const handleInvites = async (updatedDataRequest: IDataRequest, invitationsDataRequest: IDataRequest, submitRequest: boolean) => {
        // grab only the people we need to invite
        // do not send invites to anyone that already has been invited or anyone who already has access
        const invitations = await getInvitations(invitationsDataRequest);

        if (invitations?.length > 0) {
            dataRequestsService
                .bulkInviteTeamMembers(updatedDataRequest.id, invitations)
                .pipe(first())
                .subscribe(
                    (resp) => {
                        handleDataRequestSaved(resp.dataRequest, submitRequest);
                    },
                    (error) => {
                        modalService.error(error);

                        if (invitations.length > 1) {
                            // pull down the latest data request instance in this case
                            // the data request could have been modified prior to the fail since bulk invite can potentially
                            // have multiple invites / data request updates, and on error it does not return the latest DR
                            dataRequestsService
                                .getDataRequest(updatedDataRequest.id)
                                .pipe(first())
                                .subscribe((resultDataRequest) => handleDataRequestSaved(resultDataRequest, submitRequest));
                        } else {
                            handleDataRequestSaved(updatedDataRequest, submitRequest);
                        }
                    }
                );
        } else {
            handleDataRequestSaved(updatedDataRequest, submitRequest);
        }
    };

    const handleFormSave = (submitRequest = false) => {
        // already in the process of a form save, don't do any additional saves (yet)
        // also ignore if no fields have changed
        if (isSavingRef.current) {
            return;
        }

        formApi.handleSubmit(
            // handles form without validation errors (save and/or submit)
            (formValues) => handleFormApiSave(formValues, submitRequest),
            () => {
                // handles form with validation errors (save only, no submit)
                const formValues = formApi.getValues();
                handleFormApiSave(formValues, submitRequest);
            }
        )();
    };

    function checkResearchTeamForDuplicates(researchTeam: IResearchTeam) {
        const teamMembers = researchTeam.researchTeamDetails;
        const teamMemberEmails = teamMembers.map((tm) => tm.email.toLowerCase());
        const dupEmails = findDuplicates(teamMemberEmails);
        const teamMemberOrcidIds = teamMembers.filter((tm) => tm.orcidId).map((tm) => tm.orcidId.toLowerCase());
        const dupOrcidIds = findDuplicates(teamMemberOrcidIds);
        if (dupEmails.length > 0 || dupOrcidIds.length > 0) {
            let message =
                'Each user email address or ORCID iD can appear in the research team only once - please delete any duplicate users.  If a user will serve as both the Lead Researcher and Statistician, use the "Lead Researcher is also Statistician Researcher" checkbox to indicate this.' +
                `The following team members have duplicate emails or ORCID iDs: ${dupEmails.join(',')} ${dupOrcidIds.join(',')}`;
            modalService.error(message);
            return false;
        }
        return true;
    }

    const handleFormApiSave = (formValues: IDataRequest, submitRequest: boolean) => {
        let updatedDataRequest: IDataRequest = { ...formValues };

        if (isDirty) {
            // only do another request update if needed (aka data has changed)
            updateIsSaving(true);
            //check Research team for duplicates
            if (submitRequest && !isAmr && !checkResearchTeamForDuplicates(updatedDataRequest.researchTeam)) {
                //save, but not submit
                handleFormApiSave(formValues, false);
                return;
            }

            // null out the additional properties that are not being used
            // they cause json parsing issues when sent to the server for the lead and statistician
            updatedDataRequest = {
                ...updatedDataRequest,
                researchTeam: {
                    ...updatedDataRequest.researchTeam,
                    leadResearcherTeamMemberDetail: null,
                    statisticianTeamMemberDetail: null
                }
            };

            dataRequestsService
                .updateDataRequest(updatedDataRequest)
                .pipe(first())
                .subscribe((resultDataRequest) => {
                    handleInvites(resultDataRequest, updatedDataRequest, submitRequest);
                }, handleError);
        } else if (submitRequest) {
            handleConfirmStep(updatedDataRequest);
        }
    };

    const handleFormSavePublish = (submitRequest = false): Promise<IDataRequest> => {
        if (isSavingRef.current) {
            return Promise.resolve(null);
        }

        const formValues = formApi.getValues();
        let updatedDataRequest: IDataRequest = { ...formValues };

        // only do another request update if needed (aka data has changed)
        updateIsSaving(true);
        //check Research team for duplicates
        if (submitRequest && !isAmr && !checkResearchTeamForDuplicates(updatedDataRequest.researchTeam)) {
            //save, but not submit
            handleFormApiSave(formValues, false);
            return Promise.resolve(null);
        }

        // null out the additional properties that are not being used
        // they cause json parsing issues when sent to the server for the lead and statistician
        updatedDataRequest = {
            ...updatedDataRequest,
            researchTeam: {
                ...updatedDataRequest.researchTeam,
                leadResearcherTeamMemberDetail: null,
                statisticianTeamMemberDetail: null
            }
        };

        return new Promise<IDataRequest>((resolve, reject) => {
            dataRequestsService
                .updateDataRequest(updatedDataRequest)
                .pipe(first())
                .subscribe(
                    (resultDataRequest) => {
                        handleInvites(resultDataRequest, updatedDataRequest, submitRequest);
                        resolve(resultDataRequest);
                    },
                    (error) => {
                        handleError(error);
                        reject(error);
                    }
                );
        });
    };

    const handelDuaSave = () => {
        setIsSavingDataRequest(true);
        const updatedDataRequest = {
            ...dataRequestState,
            duaDetail: {
                ...duaDetails,
                contributorTemplates: planningTableData,
                duaDocuments: duaDocuments
            },
            statusUpdate: {
                ...statusUpdateState
            }
        };
        dataRequestsService.updateDataRequest(updatedDataRequest).subscribe(
            (resultDataRequest) => {
                toastService.success(`DUA Details successfully saved`);
                setDataRequest(resultDataRequest);
                setIsSavingDataRequest(false);
            },
            (err) => {
                modalService.error(`An error occurred with your operation. Please try again or contact Vivli support.`);
                setIsSavingDataRequest(false);
            }
        );
    };

    function handleSavePdfVersion() {
        setIsSavingDataRequest(true);
        dataRequestsService
            .saveDataRequestPdf(dataRequestState.id)
            .pipe(
                first(),
                finalize(() => setIsSavingDataRequest(false))
            )
            .subscribe((resultDataRequest) => {
                toastService.success(`PDF version successfully saved`);
                setDataRequest(resultDataRequest);
            }, handleError);
    }

    ///Repurposed for use with secondary
    ///doi creation or update, does this doi action
    /// and saves the request
    const handlePublishingSaveWithSecondaryDoi = () => {
        setIsSavingDataRequest(true);
        const updatedDataRequest = {
            ...dataRequestState
        };
        dataRequestsService.createOrUpdateDoi(updatedDataRequest).subscribe(
            (resultDataRequest) => {
                toastService.success(`Doi successfully saved`);
                setDataRequest(resultDataRequest);
                setIsSavingDataRequest(false);
                //necessary in case of a create, not an update, but no problem either way.
                formApi.setValue(`secondaryAnalysisDoi`, resultDataRequest.secondaryAnalysisDoi);
            },
            (err) => {
                modalService.error(`An error occurred with your operation. Please try again or contact Vivli support.`);
                setIsSavingDataRequest(false);
            }
        );
    };

    const handleEditTitleClick = () => {
        modalService.prompt('Enter a new title for your research data request', formApi.getValues('requestTitle'), {
            title: 'Research Data Request Title',
            promptLabel: 'Title',
            cancelText: 'Cancel',
            confirmText: 'Save',
            onConfirm: (newTitle) => {
                let title = formApi.getValues('requestTitle');
                if (newTitle?.comment) {
                    title = newTitle.comment;
                }
                formApi.setValue('requestTitle', title, { shouldDirty: true });
                handleFormSave();
            }
        });
    };

    const getResetToDraftVisibility = () => {
        switch (dataRequestState.status) {
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.RejectedByVivli:
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.AwaitingIRPApproval:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.RevisionsRequestedByIRP:
            case DataRequestStatusEnum.RejectedByIRP:
            case DataRequestStatusEnum.Approved:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.DUAValidated:
            case DataRequestStatusEnum.DUAReturnedToRequester:
                if (isVivliAdmin) {
                    //TODO PERMFE - redo with permissions+
                    return true;
                }
                break;
            case DataRequestStatusEnum.Cancelled:
                return true;
            case DataRequestStatusEnum.Fulfilled:
            case DataRequestStatusEnum.PartiallyFulfilled:
                if (!virtualMachine && isVivliAdmin) {
                    //TODO PERMFE - redo with permissions+
                    return true;
                }
                break;
            case DataRequestStatusEnum.Draft:
            case DataRequestStatusEnum.Archived:
            default:
                return false;
        }
    };

    const dateEditingEnabled = useMemo(() => {
        return dataRequestState.status === DataRequestStatusEnum.Draft || isVivliAdmin;
    }, [dataRequestState.status]);
    //End section

    const provider = {
        dataRequest: dataRequestState,
        updateDataRequest: (dataRequest) => {
            const updatedDataRequest = transformDataRequest(dataRequest);
            updateMyDataRequest(updatedDataRequest);
            setDataRequest(updatedDataRequest);
            formApi.reset(updatedDataRequest, { keepErrors: true, keepIsValid: true });
        },
        showApprovalButtons,
        setShowApprovalButtons,
        organizations,
        virtualMachine,
        showResearchResults,
        isUpdatingStatus,
        setIsUpdatingStatus,
        handleEditTitleClick,
        isSavingDataRequest,
        isSubmittingDataRequest,
        handleFormSave,
        handleFormSavePublish,
        handleDataPackageCreation,
        getResetToDraftVisibility,
        updateIsSaving,
        isAdminEditing,
        setIsAdminEditing,
        ownsDataRequest,
        statusUpdateSignal,
        setStatusUpdateSignal,
        handelDuaSave,
        handlePublishingSaveWithSecondaryDoi,
        planningTableData,
        setPlanningTableData,
        duaDetails,
        setDuaDetails,
        statusUpdateState,
        setStatusUpdateState,
        duaDocuments,
        setDuaDocuments,
        includeRiskScore,
        setIncludeRiskScore,
        surveyHasErrors,
        setSurveyHasErrors,
        handleSavePdfVersion,
        dateEditingEnabled,
        isActiveUserOrgAdmin
    };

    useEffect(() => {
        if (!dataRequestState) {
            return;
        }

        updateShowResearchResults();
        updateShowApprovalButtons();
    }, [dataRequestState]);

    useEffect(() => {
        if (!virtualMachine && dataRequest) {
            virtualMachineService.getDataRequestVm(dataRequest.id).pipe(first()).subscribe(setVirtualMachine, modalService.error);
        }
    }, [dataRequest]);

    useEffect(() => {
        updateShowResearchResults();
    }, [virtualMachine]);

    useEffect(() => {
        if (dataRequestState?.duaDetail?.contributorTemplates) {
            setPlanningTableData(dataRequestState.duaDetail.contributorTemplates);
            setDuaDetails(dataRequestState.duaDetail);
        }
        dataRequestState.duaDetail?.duaDocuments && setDuaDocuments(dataRequestState.duaDetail.duaDocuments);
        dataRequestState.statusUpdate && setStatusUpdateState(dataRequestState.statusUpdate);
    }, [dataRequestState]);

    return <DataRequestContext.Provider value={provider}>{children}</DataRequestContext.Provider>;
};
